import { useLayoutEffect, useState } from "react";
import { apiMutate, useApi } from "../hooks/useApi";
import { mailfehlerText } from "../lib/mailfehler-anzeige";
import { Button, ErrorNote, Icon, Input, LoadingRows, Modal, Toggle, Textarea } from "./components";
import { anlassAnzeige, formatDate } from "./format";
import { useVarianteWahl } from "./VarianteWaehlenDialog";

/** sql/763 (Zusatz-Schutz): wortgleich zur Meldung des Servers (`NICHTS_GEWAEHLT_TELEFONISCH`, routes/events.ts). */
const NICHTS_GEWAEHLT_TELEFONISCH =
  "Diese Variante besteht nur aus Zubuchungen. Bitte im Angebots-Builder festlegen, was gebucht wird, oder die Annahme über die Gastseite abschließen lassen.";

/**
 * Punkt 96 (Antoine, 23.09.2026): „bei Klick fragen: Bestätigung an den Gast schicken — mit einem
 * Mailtext, den man aber auch komplett wegklicken kann, so wie es bei ‚Erinnerung senden' ist."
 *
 * Dasselbe Interaktionsmuster wie `ErinnerungSendenDialog`: Vorschlagswerte vom Server
 * (`GET …/telefonisch-angenommen/vorschau`, Vorlage `zusage_bestaetigung`), Betreff und Text frei
 * änderbar. Zusätzlich lässt sich die Mail ganz abwählen — dann wird angenommen, aber der Gast
 * bekommt nichts. Anzahlungslink bzw. Kostenübernahme-Hinweis, Gruß und Signatur hängt der
 * Server an; sie stehen erst nach der Annahme fest.
 */

interface Vorschau {
  subject: string;
  text: string;
  toAddr: string | null;
  customerName: string | null;
  occasion: string | null;
  eventDate: string | null;
  guestCount: number | null;
}

/** sql/755 (dritte Pruefrunde): ein bepreister Wahlgang, dessen Gäste noch zu verteilen sind (409 der Annahme). */
export interface OffenerWahlgang {
  gruppe: string;
  gang: string | null;
  soll: number;
  gerichte: { id: string; name: string; max: number; menge: number }[];
}

/** Summe = Soll und kein Gericht über seiner Obergrenze — dieselbe Prüfung wie die Annahme. */
export function wahlVerteilungOk(wahlgaenge: readonly OffenerWahlgang[], mengen: Record<string, Record<string, number>>): boolean {
  return wahlgaenge.every((w) => {
    const summe = w.gerichte.reduce((s, g) => s + (mengen[w.gruppe]?.[g.id] ?? 0), 0);
    return summe === w.soll && w.gerichte.every((g) => (mengen[w.gruppe]?.[g.id] ?? 0) <= g.max);
  });
}

export interface TelefonischAngenommenErgebnis {
  paymentPathway?: "stripe" | "kostenuebernahme";
  gastMail?: { emailSent: boolean; emailError: string | null } | null;
}

export function TelefonischAngenommenDialog({
  open,
  onClose,
  eventId,
  onAngenommen,
  onFehler,
}: {
  open: boolean;
  onClose: () => void;
  eventId: string | undefined;
  onAngenommen: (r: TelefonischAngenommenErgebnis) => void;
  /** Fehler der Annahme selbst — die Seite übersetzt die bekannten Codes wie bisher. */
  onFehler: (message: string) => void;
}) {
  // 26.10.2026 (V-1228): mehrere aktive Varianten ohne Wahl -> der Server fragt (422 variante_waehlen).
  const { mitVarianteWahl, dialog: varianteDialog } = useVarianteWahl();
  const vorschauQ = useApi<Vorschau>(open && eventId ? `/api/events/${eventId}/telefonisch-angenommen/vorschau` : null);
  const [mitMail, setMitMail] = useState(true);
  const [subject, setSubject] = useState("");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [mailFehler, setMailFehler] = useState<string | null>(null);
  // Punkt 106: ein unbekannter Platzhalter im Text — der Server hat NICHT angenommen, der Dialog
  // bleibt offen, damit der Betreiber korrigieren kann.
  const [textFehler, setTextFehler] = useState<string | null>(null);
  // sql/755: die Annahme sperrt, bis die Gäste eines bepreisten Wahlgangs verteilt sind — dann hier eintragen.
  const [wahlgaenge, setWahlgaenge] = useState<OffenerWahlgang[] | null>(null);
  const [wahlMengen, setWahlMengen] = useState<Record<string, Record<string, number>>>({});
  const [wahlHinweis, setWahlHinweis] = useState<string | null>(null);

  useLayoutEffect(() => {
    if (open) {
      setBusy(false);
      setMailFehler(null);
      setTextFehler(null);
      setWahlgaenge(null);
      setWahlMengen({});
      setWahlHinweis(null);
    }
  }, [open]);

  // Nur beim ersten Laden nach dem Öffnen vorbefüllen — wie im Erinnerungs-Dialog.
  const [befuelltFuer, setBefuelltFuer] = useState<string | null>(null);
  if (open && eventId && vorschauQ.data && befuelltFuer !== eventId) {
    setBefuelltFuer(eventId);
    setSubject(vorschauQ.data.subject);
    setText(vorschauQ.data.text);
    setMitMail(Boolean(vorschauQ.data.toAddr));
  }
  if (!open && befuelltFuer !== null) setBefuelltFuer(null);

  const toAddr = vorschauQ.data?.toAddr ?? null;
  const name = vorschauQ.data?.customerName ?? "Kunde";
  const mailUnvollstaendig = mitMail && (!subject.trim() || !text.trim());
  const wahlUnvollstaendig = wahlgaenge !== null && !wahlVerteilungOk(wahlgaenge, wahlMengen);

  async function annehmen() {
    if (!eventId) return;
    setBusy(true);
    setTextFehler(null);
    try {
      const r = await mitVarianteWahl((chosenOptionId) =>
        apiMutate<TelefonischAngenommenErgebnis>(
          `/api/events/${eventId}/telefonisch-angenommen`,
          "POST",
          {
            ...(mitMail ? { mail: { betreff: subject, text } } : { mail: null }),
            ...(chosenOptionId ? { chosenOptionId } : {}),
            // Vierte Pruefrunde: die Gaeste je Gericht reisen MIT der Annahme - der Server speichert sie erst dann und nimmt sie
            // bei einer misslungenen Annahme zurueck (kein halber Stand).
            ...(wahlgaenge ? { wahl: wahlMengen } : {}),
          },
        ),
      );
      // Abgebrochen bei der Variantenfrage: nichts angenommen, Dialog bleibt offen.
      if (r === undefined) return;
      onAngenommen(r);
      if (r?.gastMail && !r.gastMail.emailSent) {
        // Angenommen ist angenommen — der Betreiber muss aber erfahren, dass der Gast nichts bekam.
        setMailFehler(r.gastMail.emailError);
      } else {
        onClose();
      }
    } catch (err) {
      const code = (err as { code?: string }).code;
      if (code === "unbekannte_platzhalter" || code === "leer_nach_ersetzung") {
        setTextFehler(err instanceof Error ? err.message : String(err));
        return;
      }
      const offen = (err as { body?: { wahlgaenge?: OffenerWahlgang[] } }).body?.wahlgaenge;
      if ((code === "menuwahl_unvollstaendig" || code === "wahl_menge_ungueltig") && offen?.length) {
        // Nichts angenommen — der Dialog bleibt offen und fragt die Gäste je Gericht ab (vorbelegt mit dem Stand des Gastes).
        // Hat der Betreiber schon eingetragen, bleibt seine Eingabe stehen (der Server hat sie zurueckgenommen).
        if (!wahlgaenge) {
          setWahlgaenge(offen);
          setWahlMengen(Object.fromEntries(offen.map((w) => [w.gruppe, Object.fromEntries(w.gerichte.map((g) => [g.id, g.menge]))])));
        }
        setWahlHinweis(err instanceof Error ? err.message : String(err));
        return;
      }
      // sql/763 Zusatz-Schutz: Variante nur aus Zubuchungen, nichts gewaehlt - der feste Hinweis, was zu tun ist.
      if (code === "nichts_gewaehlt") {
        onFehler(NICHTS_GEWAEHLT_TELEFONISCH);
        onClose();
        return;
      }
      onFehler(err instanceof Error ? err.message : String(err));
      onClose();
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
    <Modal
      open={open}
      onClose={onClose}
      width="560px"
      title={mailFehler !== null ? "Angenommen — Bestätigung NICHT versendet" : "Telefonisch angenommen"}
      subtitle={
        !vorschauQ.loading && !vorschauQ.error && mailFehler === null
          ? [name, anlassAnzeige(vorschauQ.data?.occasion), vorschauQ.data?.eventDate ? formatDate(vorschauQ.data.eventDate) : null, vorschauQ.data?.guestCount ? `${vorschauQ.data.guestCount} Gäste` : null]
              .filter(Boolean)
              .join(" · ")
          : undefined
      }
      footer={
        vorschauQ.loading ? undefined : vorschauQ.error || mailFehler !== null ? (
          <Button type="button" variant="primary" onClick={onClose}>
            Schließen
          </Button>
        ) : (
          <>
            <Button type="button" variant="secondary" onClick={onClose} disabled={busy}>
              Abbrechen
            </Button>
            <Button type="button" data-testid="telefonisch-annehmen" onClick={() => void annehmen()} disabled={busy || mailUnvollstaendig || wahlUnvollstaendig}>
              <Icon name={mitMail ? "send" : "check"} className="text-[16px]" />{" "}
              {busy ? "Wird verbucht …" : mitMail ? "Annehmen und senden" : "Annehmen ohne Mail"}
            </Button>
          </>
        )
      }
    >
      {vorschauQ.loading ? (
        <LoadingRows rows={3} />
      ) : vorschauQ.error ? (
        <ErrorNote message={vorschauQ.error} />
      ) : mailFehler !== null ? (
        <div className="space-y-3">
          <p className="text-[12.5px] text-secondary-fixed-dim">
            {name} hat <strong>keine E-Mail</strong> erhalten.
          </p>
          <p className="w-full rounded-lg bg-error/10 px-3 py-2 text-left text-[11px] font-mono text-error dark:text-[#e39b92]">{mailfehlerText(mailFehler)}</p>
        </div>
      ) : (
          <div className="space-y-3.5">
            <div className="flex items-center gap-2.5">
              <Toggle checked={mitMail} onChange={setMitMail} label="Bestätigung an den Gast schicken" disabled={!toAddr || busy} />
              <span className="text-[13px] font-semibold text-on-background dark:text-inverse-on-surface">Bestätigung an den Gast schicken</span>
              <span className="truncate text-[12px] text-secondary-fixed-dim">{toAddr ?? "keine E-Mail hinterlegt"}</span>
            </div>

            {mitMail ? (
              <>
                <label className="block">
                  <span className="mb-1 block text-[12px] font-semibold text-on-background dark:text-inverse-on-surface">Betreff</span>
                  <Input data-testid="telefonisch-betreff" className="!py-2.5" value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={300} disabled={busy} />
                </label>
                <label className="block">
                  <span className="mb-1 block text-[12px] font-semibold text-on-background dark:text-inverse-on-surface">Nachricht</span>
                  <Textarea
                    data-testid="telefonisch-nachricht"
                    className="min-h-[160px] text-[12.5px] leading-relaxed"
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    maxLength={10000}
                    disabled={busy}
                  />
                  <p className="mt-1 text-[10.5px] text-secondary-fixed-dim">
                    Darunter folgen automatisch der Anzahlungslink bzw. der Hinweis zur Kostenübernahme, Gruß und Signatur.
                  </p>
                </label>
              </>
            ) : (
              <p className="text-[12px] text-secondary-fixed-dim">Das Angebot wird angenommen, der Gast bekommt keine E-Mail.</p>
            )}
            {wahlgaenge ? (
              <div className="space-y-3 rounded-lg border border-outline-variant/60 px-3 py-3 dark:border-white/10" data-testid="telefonisch-wahl">
                <span className="block text-[12px] font-semibold text-on-background dark:text-inverse-on-surface">Gäste je Gericht</span>
                {wahlHinweis ? <p className="text-[12px] text-secondary-fixed-dim">{wahlHinweis}</p> : null}
                {wahlgaenge.map((w) => {
                  const summe = w.gerichte.reduce((n, g) => n + (wahlMengen[w.gruppe]?.[g.id] ?? 0), 0);
                  const rest = w.soll - summe;
                  return (
                    <div key={w.gruppe} className="space-y-1.5">
                      <div className="flex items-baseline justify-between gap-2 text-[12px]">
                        <span className="font-semibold text-on-background dark:text-inverse-on-surface">{w.gang ?? "Auswahl"}</span>
                        <span className={rest === 0 ? "text-secondary-fixed-dim" : "text-error dark:text-[#e39b92]"}>
                          {rest === 0 ? `${w.soll} von ${w.soll} Gästen verteilt` : rest > 0 ? `noch ${rest} von ${w.soll} Gästen offen` : `${-rest} zu viel`}
                        </span>
                      </div>
                      {w.gerichte.map((g) => (
                        <label key={g.id} className="flex items-center justify-between gap-3">
                          <span className="truncate text-[12.5px] text-on-background dark:text-inverse-on-surface">{g.name}</span>
                          <Input
                            type="number"
                            inputMode="numeric"
                            min={0}
                            max={g.max}
                            aria-label={`Gäste für ${g.name}`}
                            data-testid={`telefonisch-wahl-${g.id}`}
                            className="!w-[88px] !py-1.5 text-right"
                            value={String(wahlMengen[w.gruppe]?.[g.id] ?? 0)}
                            disabled={busy}
                            onChange={(e) => {
                              const n = Math.max(0, Math.min(g.max, Math.floor(Number(e.target.value) || 0)));
                              setWahlMengen((alt) => ({ ...alt, [w.gruppe]: { ...(alt[w.gruppe] ?? {}), [g.id]: n } }));
                            }}
                          />
                        </label>
                      ))}
                    </div>
                  );
                })}
              </div>
            ) : null}
            {textFehler ? <ErrorNote message={textFehler} /> : null}
          </div>
      )}
    </Modal>
    {varianteDialog}
    </>
  );
}
